import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import * as axios from "axios";
import React, { type ReactNode, useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { SubmissionError } from "redux-form";
import type {
  ThunkDispatchType,
  ThunkResult,
} from "src/app/reducers/IMainAppState";
import { RemoveAuthenticationToken } from "src/authentication/actions/AuthenticationActionCreator";
import { useRemoveAuthenticationToken } from "src/authentication/hooks/useRemoveAuthenticationToken";
import type { AuthenticationToken } from "src/authentication/objects/AuthenticationToken";
import { FieldValidationErrorMessage } from "src/shared/actions/components/FieldValidationErrorMessage";
import { ResponseError } from "src/shared/actions/objects/ResponseError";
import ReactNodeError from "src/shared/objects/ReactNodeError";
import {
  GetPathWithParams,
  type QueryParams,
} from "src/utilities/URLParamsUtils";

export type FormValidationError = { code: string; detail?: string };
export type FormValidationErrorHandler = (
  response: AxiosResponse,
  error: FormValidationError,
  fieldName?: string,
  fieldErrorMessage?: string,
) => void;

/**
 * @deprecated
 */
export function APIGet(
  url: string,
  requestInit?: RequestInit,
  authenticationToken?: AuthenticationToken,
): ThunkResult<Promise<Response>> {
  return async (dispatch) => {
    return await dispatch(
      RaangeFetch(url, {
        ...requestInit,
        method: "get",
        headers: dispatch(getHeaders(authenticationToken)),
      }),
    );
  };
}

/**
 * @deprecated
 */
export function APIPost(
  url: string,
  requestInit?: RequestInit,
  authenticationToken?: AuthenticationToken,
): ThunkResult<Promise<Response>> {
  return async (dispatch) => {
    return await dispatch(
      RaangeFetch(url, {
        ...requestInit,
        method: "post",
        headers: dispatch(getHeaders(authenticationToken)),
      }),
    );
  };
}

/**
 * @deprecated
 */
export function APIPut(
  url: string,
  requestInit?: RequestInit,
  authenticationToken?: AuthenticationToken,
): ThunkResult<Promise<Response>> {
  return async (dispatch) => {
    return await dispatch(
      RaangeFetch(url, {
        ...requestInit,
        method: "put",
        headers: dispatch(getHeaders(authenticationToken)),
      }),
    );
  };
}

function RaangeFetch(
  input: RequestInfo,
  init?: RequestInit,
): ThunkResult<Promise<Response>> {
  return async (dispatch, getState) => {
    const response: Response = await fetch(input, init);
    if (!response.ok) {
      const errorObj: { code: string; detail?: string } = await response.json();
      if (response.status === 400 && errorObj.detail) {
        const regex: RegExp = new RegExp("body.([a-zA-Z]+)");
        const fieldNameMatches = errorObj.detail.match(regex);
        if (fieldNameMatches) {
          const fieldName = fieldNameMatches[1];
          throw new SubmissionError({
            [fieldName]: errorObj.detail,
          });
        } else {
          throw new SubmissionError({
            _error: errorObj.detail,
          });
        }
      } else if (response.status === 403) {
        // Remove authentication token on 403 so that it would redirect to login screen.
        dispatch(RemoveAuthenticationToken());
      }

      throw new Error(errorObj.code);
    }
    return response;
  };
}

function getHeaders(
  authenticationToken?: AuthenticationToken,
): ThunkResult<Headers> {
  return (dispatch, getState) => {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // AuthenticationToken Token.
    authenticationToken =
      authenticationToken || getState().authentication.authenticationToken;
    if (authenticationToken !== undefined) {
      headers.Authorization =
        "Bearer " + authenticationToken.authenticationToken;
    }

    return new Headers(headers);
  };
}

export const useApiCall = () => {
  const dispatch = useDispatch<ThunkDispatchType>();
  return useMemo(
    () => ({
      getConfig: (config?: AxiosRequestConfig) => dispatch(getConfig(config)),
    }),
    [dispatch],
  );
};

export const useApiGet = () => {
  const { getConfig } = useApiCall();
  const removeAuthenticationToken = useRemoveAuthenticationToken();
  return useCallback(
    async <T,>(
      url: string,
      params?: QueryParams,
      axiosConfig?: AxiosRequestConfig,
    ) => {
      return handleAxiosRequest(
        axios.default.get<T>(
          GetPathWithParams(url, params),
          getConfig(axiosConfig),
        ),
        {
          removeAuthenticationToken,
          isNewErrorCodeHandling: TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
        },
      );
    },
    [getConfig, removeAuthenticationToken],
  );
};

export const useApiPost = () => {
  const { getConfig } = useApiCall();
  const removeAuthenticationToken = useRemoveAuthenticationToken();
  return useCallback(
    async <T,>(
      url: string,
      data?: unknown,
      {
        messagesOverride,
        ...axiosConfig
      }: AxiosRequestConfig & Pick<RequestConfig, "messagesOverride"> = {},
    ) => {
      return handleAxiosRequest(
        axios.default.post<T>(url, data, getConfig(axiosConfig)),
        {
          removeAuthenticationToken,
          isNewErrorCodeHandling: TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
          messagesOverride,
        },
      );
    },
    [getConfig, removeAuthenticationToken],
  );
};

export const useApiPut = () => {
  const { getConfig } = useApiCall();
  const removeAuthenticationToken = useRemoveAuthenticationToken();
  return useCallback(
    async <T,>(
      url: string,
      data?: unknown,
      {
        messagesOverride,
        ...axiosConfig
      }: AxiosRequestConfig & Pick<RequestConfig, "messagesOverride"> = {},
    ) => {
      return handleAxiosRequest(
        axios.default.put<T>(url, data, getConfig(axiosConfig)),
        {
          removeAuthenticationToken,
          isNewErrorCodeHandling: TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
          messagesOverride,
        },
      );
    },
    [getConfig, removeAuthenticationToken],
  );
};

/**
 * @deprecated
 */
export function APIGetAxios<T>(
  url: string,
  config?: AxiosRequestConfig,
  authenticationToken?: AuthenticationToken,
  isNewErrorCodeHandling?: boolean,
): ThunkResult<Promise<AxiosResponse<T>>> {
  return async (dispatch) => {
    config = dispatch(getConfig(config, authenticationToken));
    return handleAxiosRequest(axios.default.get(url, config), {
      removeAuthenticationToken: removeAuthenticationTokenHandler(dispatch),
      isNewErrorCodeHandling,
    });
  };
}

/**
 * @deprecated
 */
export function APIPostAxios<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
  authenticationToken?: AuthenticationToken,
  isNewErrorCodeHandling?: boolean,
  formValidationErrorHandler?: FormValidationErrorHandler,
): ThunkResult<Promise<AxiosResponse<T>>> {
  return async (dispatch) => {
    config = dispatch(getConfig(config, authenticationToken));
    return handleAxiosRequest(axios.default.post(url, data, config), {
      removeAuthenticationToken: removeAuthenticationTokenHandler(dispatch),
      isNewErrorCodeHandling,
      formValidationErrorHandler,
    });
  };
}

/**
 * @deprecated
 */
export function APIPutAxios<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
  authenticationToken?: AuthenticationToken,
  isNewErrorCodeHandling?: boolean,
  formValidationErrorHandler?: FormValidationErrorHandler,
): ThunkResult<Promise<AxiosResponse<T>>> {
  return async (dispatch) => {
    config = dispatch(getConfig(config, authenticationToken));
    return handleAxiosRequest(axios.default.put(url, data, config), {
      removeAuthenticationToken: removeAuthenticationTokenHandler(dispatch),
      isNewErrorCodeHandling,
      formValidationErrorHandler,
    });
  };
}

interface RequestConfig {
  removeAuthenticationToken: () => void;
  isNewErrorCodeHandling?: boolean;
  formValidationErrorHandler?: FormValidationErrorHandler;
  messagesOverride?: {
    alreadyExistsNoField?: ReactNode;
  };
}

async function handleAxiosRequest<T>(
  responsePromise: Promise<AxiosResponse<T>>,
  {
    removeAuthenticationToken,
    isNewErrorCodeHandling = false,
    formValidationErrorHandler,
    messagesOverride,
  }: RequestConfig,
): Promise<AxiosResponse<T>> {
  const response: AxiosResponse = await (async () => {
    try {
      return await responsePromise;
    } catch (ex) {
      return (ex as AxiosError).response;
    }
  })();

  // TODO: Remove after migration
  if (!isNewErrorCodeHandling) {
    if (response.status !== 200) {
      const errorObj: { code: string; detail?: string } = response.data;
      if (response.status === 400) {
        if (errorObj.detail) {
          const regex: RegExp = new RegExp("^body.([a-zA-Z]+)\\s?(.*?)$");
          const fieldNameMatches = errorObj.detail.match(regex);
          if (fieldNameMatches) {
            const fieldName = fieldNameMatches[1];
            throw new SubmissionError({
              [fieldName]: fieldNameMatches[2],
            });
          } else {
            throw new SubmissionError({
              _error: errorObj.detail,
            });
          }
        } else if (errorObj.code === "INVALID_FILE_CONTENT") {
          throw new ReactNodeError(
            <FormattedMessage id="fetch.error.file.invalid" />,
          );
        }
      } else if (response.status === 403) {
        // Remove authentication token on 403 so that it would redirect to login screen.
        removeAuthenticationToken();
      } else if (response.status === 413) {
        throw new ReactNodeError(
          <FormattedMessage id="fetch.error.file.too_large" />,
        );
      }

      throw new Error(errorObj.code);
    }
  } else {
    // New Handling.

    // If response is as a result of connection error
    if (!response) {
      throw new ReactNodeError(
        <FormattedMessage id="fetch.error.request_cannot_be_processed" />,
      );
    } else if (response.status !== 200) {
      const errorObj: FormValidationError = response.data;

      // Get detail field errors
      const { fieldName, fieldErrorMessage } = (() => {
        if (errorObj.detail) {
          // The detail matches the format "body.FIELD_NAME MESSAGE"
          const regex: RegExp = new RegExp("^body.([a-zA-Z]+)\\s?(.*?)$");
          const fieldNameMatches = errorObj.detail.match(regex);
          if (fieldNameMatches) {
            return {
              fieldName: fieldNameMatches[1],
              fieldErrorMessage: fieldNameMatches[2],
            };
          }
        }
        return { fieldName: undefined, fieldErrorMessage: undefined };
      })();

      formValidationErrorHandler?.(
        response,
        errorObj,
        fieldName,
        fieldErrorMessage,
      );

      if (response.status === 400) {
        // Code = BAD_REQUEST
        if (
          errorObj.code === "BAD_REQUEST" ||
          errorObj.code === "INVALID_CSV_HEADER"
        ) {
          // TODO: can use above regex values.
          // detail field exists
          if (errorObj.detail) {
            // The detail matches the format "body.FIELD_NAME MESSAGE"
            const regex: RegExp = new RegExp("^body.([a-zA-Z]+)\\s?(.*?)$");
            const fieldNameMatches = errorObj.detail.match(regex);
            if (fieldNameMatches) {
              const fieldName = fieldNameMatches[1];
              throw new SubmissionError({
                _error: <FormattedMessage id="fetch.error.bad_request" />,
                [fieldName]: (
                  <FieldValidationErrorMessage
                    code={errorObj.code}
                    serverSideErrorMessage={fieldNameMatches[2]}
                  />
                ),
              });
            }
          }

          // If detail field doesn't exist, or field url cannot be matched
          // Throw generic message
          throw new SubmissionError({
            _error: <FormattedMessage id="fetch.error.bad_request" />,
          });
        }

        // Code = INVALID_FILE_CONTENT
        else if (errorObj.code === "INVALID_FILE_CONTENT") {
          throw new ResponseError(
            response,
            <FormattedMessage id="fetch.error.file.invalid" />,
          );
        }

        // Code = ALREADY_EXISTS
        else if (errorObj.code === "ALREADY_EXISTS") {
          // detail field exists
          if (errorObj.detail) {
            // TODO: can use above regex values.
            // The detail matches the format "body.FIELD_NAME MESSAGE"
            const regex = /^body\.([a-zA-Z]+).*?$/;
            const fieldNameMatches = errorObj.detail.match(regex);
            if (fieldNameMatches) {
              const fieldName = fieldNameMatches[1];
              throw new SubmissionError({
                _error: <FormattedMessage id="fetch.error.bad_request" />,
                [fieldName]: (
                  <FormattedMessage id="fetch.error.already_exists_field" />
                ),
              });
            }
          }

          // If detail field doesn't exist, or field url cannot be matched
          // Throw generic message
          throw new SubmissionError({
            _error: messagesOverride?.alreadyExistsNoField ?? (
              <FormattedMessage id="fetch.error.already_exists_no_field" />
            ),
          });
        }

        // Throw generic message
        throw new SubmissionError({
          _error: <FormattedMessage id="fetch.error.bad_request" />,
        });
      } else if (response.status === 401 || response.status === 403) {
        // Remove authentication token on 401 / 403 so that it would redirect to login screen.
        removeAuthenticationToken();
        throw new ResponseError(
          response,
          <FormattedMessage id="fetch.error.unauthorized" />,
        );
      } else if (response.status === 404) {
        throw new ResponseError(
          response,
          <FormattedMessage id="fetch.error.not_found" />,
        );
      } else if (response.status === 413) {
        throw new ResponseError(
          response,
          <FormattedMessage id="fetch.error.file.too_large" />,
        );
      } else if (response.status === 409) {
        if (errorObj.code === "VOLUME_LIMIT_REACHED") {
          throw new SubmissionError({
            _error: <FormattedMessage id="fetch.error.volumeLimitReached" />,
          });
        }
      } else if (response.status === 500) {
        throw new ResponseError(
          response,
          <FormattedMessage id="fetch.error.internal_server_error" />,
        );
      } else {
        throw new ResponseError(
          response,
          <FormattedMessage id="fetch.error.request_cannot_be_processed" />,
        );
      }
    }
    // if response is 200 but it is not json
    else if (typeof response.data === "string") {
      throw new ResponseError(
        response,
        <FormattedMessage id="fetch.error.request_cannot_be_processed" />,
      );
    }
  }

  return response;
}

function getConfig(
  config?: AxiosRequestConfig,
  authenticationToken?: AuthenticationToken,
): ThunkResult<AxiosRequestConfig> {
  return (dispatch, getState) => {
    if (!config) {
      config = {};
    }

    const headers = (config.headers = config.headers || {});

    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    // AuthenticationToken Token.
    authenticationToken =
      authenticationToken || getState().authentication.authenticationToken;
    if (authenticationToken && !headers["Authorization"]) {
      headers["Authorization"] =
        "Bearer " + authenticationToken.authenticationToken;
    }

    return config;
  };
}

export const TEMP_AXIOS_NEW_ERROR_CODE_HANDLING = true;

export const removeAuthenticationTokenHandler = (
  dispatch: ThunkDispatchType,
) => {
  return () => dispatch(RemoveAuthenticationToken());
};
